import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { map } from 'rxjs/operators';

import { APIServices } from '@nx-ws/bhn-enums/api-services.enum';

import { PageDto } from './../shared/page.dto';
import { PageQueriesModel } from './../shared/page-queries.model';
import { MerchantIdentifierDto } from './merchant-identifier.dto';

export type MerchantIdentifiersRequestData = {
  clientId: string;
  channelId: string;
  id?: string;
  mid?: MerchantIdentifierDto;
};

@Injectable()
export class MerchantIdentifiersRestService {
  private apiUrl = `api/${APIServices.CHANNELS}`;

  constructor(private http: HttpClient) {}

  list(
    queries: PageQueriesModel,
    { clientId, channelId }: MerchantIdentifiersRequestData
  ) {
    const url = this.apiListUrl(clientId, channelId);
    const params = {
      params: queries.getParams()
    };

    return this.http.get<PageDto<MerchantIdentifierDto>>(url, params).pipe(
      map(d => {
        const page = new PageDto(d);
        page.content = d.content.map(
          (i: MerchantIdentifierDto) => new MerchantIdentifierDto(i)
        );
        return page;
      })
    );
  }

  get(id: string) {
    const url = this.apiListUrl() + '/' + id;

    return this.http
      .get<MerchantIdentifierDto>(url)
      .pipe(map(data => new MerchantIdentifierDto(data)));
  }

  getByClientAndChannel({
    id,
    clientId,
    channelId
  }: MerchantIdentifiersRequestData) {
    const url =
      this.getListUrlByClientAndChannel(clientId, channelId) + '/' + id;
    return this.http
      .get<MerchantIdentifierDto>(url)
      .pipe(map(data => new MerchantIdentifierDto(data)));
  }

  post({ mid, clientId, channelId }: MerchantIdentifiersRequestData) {
    const url = this.getListUrlByClientAndChannel(clientId, channelId);
    return this.http
      .post<MerchantIdentifierDto>(url, mid)
      .pipe(map(data => new MerchantIdentifierDto(data)));
  }

  put({ mid, clientId, channelId, id }: MerchantIdentifiersRequestData) {
    const url =
      this.getListUrlByClientAndChannel(clientId, channelId) + '/' + id;
    return this.http
      .put<MerchantIdentifierDto>(url, mid)
      .pipe(map(data => new MerchantIdentifierDto(data)));
  }

  patch({ mid, clientId, channelId, id }: MerchantIdentifiersRequestData) {
    const url =
      this.getListUrlByClientAndChannel(clientId, channelId) + '/' + id;
    return this.http
      .patch<MerchantIdentifierDto>(url, mid)
      .pipe(map(data => new MerchantIdentifierDto(data)));
  }

  delete({ clientId, channelId, id }: MerchantIdentifiersRequestData) {
    const url =
      this.getListUrlByClientAndChannel(clientId, channelId) + '/' + id;
    return this.http.delete(url);
  }

  private apiListUrl(clientId?: string, channelId?: string) {
    return clientId && channelId
      ? this.getListUrlByClientAndChannel(clientId, channelId)
      : `${this.apiUrl}/merchantidentifiers`;
  }

  private getListUrlByClientAndChannel(clientId: string, channelId: string) {
    return `${
      this.apiUrl
    }/clients/${clientId}/channels/${channelId}/merchantidentifiers`;
  }
}
